import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ClipboardCopy,
  MessageCircle,
  Car,
  Users,
  Send,
  Mail,
  AlertTriangle,
  ChevronDown,
  ShieldAlert,
  Lock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { SITE } from "../config/site";

const inputCls =
  "w-full bg-transparent border border-[#27272A] rounded-sm p-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none";
const labelCls = "block text-xs uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] mb-1.5";
const OWNER_KEY = "ebc-owner-whatsapp";
const PIN_KEY = "ebc-owner-pin";

/**
 * OWNER JOB MANAGER — advisory workflow tool.
 * Opened via MANAGE THIS BOOKING links carrying only an opaque key + broad
 * areas. Full passenger details are fetched from the backend with key + owner
 * PIN (POST body only — never in URLs). Group messages stay area-only.
 * Status transitions are recorded server-side; an already-confirmed booking
 * cannot be confirmed twice (the server rejects it).
 */

function normalisePhone(p) {
  const digits = (p || "").replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits.slice(1);
  if (digits.startsWith("0")) return "44" + digits.slice(1);
  return digits;
}

async function ownerPost(path, payload) {
  const r = await fetch(`/api/owner/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, detail: data.detail || "", data };
}

function Section({ title, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-[#27272A] bg-[#0B0B0B]" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="focus-gold flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-base font-semibold text-white">{title}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-[#D4AF37] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && <div className="border-t border-[#27272A] p-5">{children}</div>}
    </section>
  );
}

export default function JobShare() {
  const [searchParams] = useSearchParams();
  const [fields, setFields] = useState({ reference: "", date: "", time: "", from: "", to: "", passengers: "", luggage: "" });
  const [format, setFormat] = useState("short");
  const [ownerNumber, setOwnerNumber] = useState("");
  const [mode, setMode] = useState(null); // null | "self" | "drivers"
  const [priv, setPriv] = useState({ paxName: "", paxPhone: "", paxEmail: "", pickup: "", destination: "" });
  const [driver, setDriver] = useState({ name: "", phone: "" });
  const [allocChecked, setAllocChecked] = useState(false);
  const [driverVerified, setDriverVerified] = useState(false);

  // Secure lookup state
  const [bookingKey, setBookingKey] = useState("");
  const [pin, setPin] = useState("");
  const [pinInput, setPinInput] = useState("");
  const [pinNeeded, setPinNeeded] = useState(false);
  const [pinError, setPinError] = useState("");
  const [bookingStatus, setBookingStatus] = useState("manual"); // manual = no server record
  const [assignedDriver, setAssignedDriver] = useState("");
  const [statusError, setStatusError] = useState("");

  useEffect(() => {
    const p = Object.fromEntries(searchParams.entries());
    setFields((f) => ({
      reference: p.ref || f.reference,
      date: p.date || f.date,
      time: p.time || f.time,
      from: p.from || f.from,
      to: p.to || f.to,
      passengers: p.passengers || f.passengers,
      luggage: p.luggage || f.luggage,
    }));
    if (p.key) setBookingKey(p.key);
    const savedNumber = window.localStorage.getItem(OWNER_KEY);
    if (savedNumber) setOwnerNumber(savedNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLookup = useCallback(async (key, pinValue) => {
    const res = await ownerPost("booking", { key, pin: pinValue });
    if (res.ok) {
      const d = res.data.data || {};
      setPriv({
        paxName: d["Passenger name"] || "",
        paxPhone: d["Mobile number"] || "",
        paxEmail: d["Email address"] || "",
        pickup: d["Pickup address"] || "",
        destination: d["Destination address"] || "",
      });
      setFields((f) => ({
        ...f,
        reference: res.data.ref || f.reference,
        date: d["Journey date"] || f.date,
        time: d["Pickup time"] || f.time,
        passengers: d["Passengers"] || f.passengers,
        luggage: d["Luggage"] || f.luggage,
      }));
      setBookingStatus(res.data.status || "received");
      setAssignedDriver(res.data.driver_name || "");
      setPinNeeded(false);
      setPinError("");
      return true;
    }
    if (res.status === 503) {
      // No PIN set yet — first use on any device creates it.
      setPinNeeded(true);
      setPinError("First time? Choose your 6-digit owner PIN to link this device.");
      return false;
    }
    if (res.status === 401 || res.status === 429) {
      setPinNeeded(true);
      setPinError(res.status === 429 ? res.detail : "Wrong PIN for this booking link.");
      return false;
    }
    setPinNeeded(true);
    setPinError("Could not load this booking (it may be older than 60 days). You can still work manually below.");
    return false;
  }, []);

  // Auto-lookup when a key arrives and a PIN is remembered on this device.
  useEffect(() => {
    if (!bookingKey) return;
    const savedPin = window.localStorage.getItem(PIN_KEY);
    if (savedPin) {
      setPin(savedPin);
      doLookup(bookingKey, savedPin);
    } else {
      setPinNeeded(true);
    }
  }, [bookingKey, doLookup]);

  const submitPin = async () => {
    const v = pinInput.trim();
    if (!/^\d{6}$/.test(v)) {
      setPinError("PIN must be exactly 6 digits.");
      return;
    }
    // Try bootstrap first (first-ever use), then plain unlock.
    const boot = await ownerPost("bootstrap", { key: bookingKey, pin: v });
    if (boot.ok || boot.status === 409) {
      const ok = await doLookup(bookingKey, v);
      if (ok) {
        setPin(v);
        window.localStorage.setItem(PIN_KEY, v);
      }
      return;
    }
    if (boot.status === 401) {
      const ok = await doLookup(bookingKey, v);
      if (ok) {
        setPin(v);
        window.localStorage.setItem(PIN_KEY, v);
      }
      return;
    }
    setPinError(boot.detail || "Could not set up the PIN. Try again.");
  };

  const clearPin = () => {
    window.localStorage.removeItem(PIN_KEY);
    setPin("");
    setPinNeeded(Boolean(bookingKey));
  };

  const transition = useCallback(
    async (status, driverName) => {
      if (!bookingKey || !pin) return true; // manual mode — nothing to record
      const res = await ownerPost("status", { key: bookingKey, pin, status, driver_name: driverName });
      if (res.ok) {
        setBookingStatus(status);
        if (driverName) setAssignedDriver(driverName);
        setStatusError("");
        return true;
      }
      setStatusError(res.detail || "Could not update the booking status.");
      return false;
    },
    [bookingKey, pin]
  );

  const saveOwnerNumber = (v) => {
    setOwnerNumber(v);
    if (v.trim()) window.localStorage.setItem(OWNER_KEY, v.trim());
    else window.localStorage.removeItem(OWNER_KEY);
  };

  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const setPrivField = (k) => (e) => setPriv((p) => ({ ...p, [k]: e.target.value }));
  const setDrvField = (k) => (e) => setDriver((d) => ({ ...d, [k]: e.target.value }));

  const route = fields.from && fields.to ? `${fields.from} → ${fields.to}` : fields.from || fields.to || "—";
  const shortRoute = route.replace(/ area(?= →|$)/g, "");
  const ref = fields.reference || "M11-___";

  const interestLine = ownerNumber.trim()
    ? `Interested? Tap to message me: https://wa.me/${normalisePhone(ownerNumber)}?text=${encodeURIComponent(
        `I'm interested in job ${ref} and agree to the £20 referral fee if I am selected and successfully complete the journey.`
      )}`
    : "Interested? Message me privately.";

  const fullMessage = `🚕 ESSEX BLACK CAB — AVAILABLE JOB
Job Ref: ${ref}
Route: ${route}
Date: ${fields.date || "—"}
Pickup: ${fields.time || "—"}
Passengers: ${fields.passengers || "—"}
Luggage: ${fields.luggage || "—"}
Passenger fare: £100 fixed — the passenger pays you directly inside the cab, by cash or card, at the end of the journey.
Referral fee: £20 payable to Essex Black Cab after the completed journey (bank transfer, within 24 hours).
Driver keeps: £80 after the referral fee.
Licensed London Black Cab drivers only.
${interestLine}
Expressing interest does not guarantee allocation — I confirm the selected driver privately.
Terms: essexblackcab.co.uk/driver-terms`;

  const shortMessage = `🚕 ESSEX BLACK CAB — £100 JOB
Ref: ${ref}
Route: ${shortRoute}
Date: ${fields.date || "—"} | Pickup: ${fields.time || "—"}
Passengers: ${fields.passengers || "—"}${fields.luggage ? ` | ${fields.luggage}` : ""}
Fare: £100 | Referral fee: £20 | Driver keeps: £80
Passenger pays driver cash/card in cab at journey end. £20 referral fee payable by bank transfer within 24 hours of completion.
Licensed London Black Cab drivers only.
${interestLine}
Expressing interest does not guarantee allocation.
Terms: essexblackcab.co.uk/driver-terms`;

  const message = format === "short" ? shortMessage : fullMessage;

  const passengerConfirm = `Essex Black Cab — Booking ${ref} CONFIRMED

Journey: ${route}
Date: ${fields.date || "—"} | Pickup: ${fields.time || "—"}
Fare: £100 fixed — pay your driver in the cab by cash or card at the end of the journey. No advance payment.
${mode === "self" ? "I will be driving you personally." : `Your driver ${driver.name || ""} will collect you.`}

Questions? Reply to this message or email ${SITE.email} quoting ${ref}.`;

  const driverDetails = `ESSEX BLACK CAB — JOB ALLOCATED TO YOU
Ref: ${ref}
Date: ${fields.date || "—"} | Pickup: ${fields.time || "—"}
Passenger: ${priv.paxName || "—"} — ${priv.paxPhone || "—"}
Pickup: ${priv.pickup || "—"}
Destination: ${priv.destination || "—"}
Passengers: ${fields.passengers || "—"} | Luggage: ${fields.luggage || "—"}
Fare: £100 — the passenger pays you in the cab, cash or card, at journey end.
Referral fee: £20 by bank transfer within 24 hours of completion. No completed journey, no fee.
Passenger details are private — do not share them.`;

  const shareWhatsApp = async () => {
    if (mode === "drivers") transition("offered"); // best-effort status record
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  };
  const copyText = async (text, note) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(note);
    } catch {
      toast.error("Copy failed — please select the text and copy it manually.");
    }
  };
  const smsLink = (to, body) => `sms:${(to || "").replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(body)}`;
  const mailLink = (to, subject, body) =>
    `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  // Confirmation sends the status transition FIRST (when a server record
  // exists): if the job is already confirmed elsewhere, the send is blocked.
  const confirmAndOpen = async (channel) => {
    const driverName = mode === "self" ? "Owner" : driver.name.trim();
    const ok = await transition("confirmed", driverName);
    if (!ok) return;
    const url =
      channel === "sms"
        ? smsLink(priv.paxPhone, passengerConfirm)
        : mailLink(priv.paxEmail, `Essex Black Cab — booking ${ref} confirmed`, passengerConfirm);
    window.location.href = url;
  };

  const canConfirmSelf = allocChecked;
  const canConfirmDriver = allocChecked && driverVerified && driver.name.trim() && driver.phone.trim();

  const ConfirmButtons = ({ enabled }) => {
    const gated = enabled && bookingStatus !== "confirmed";
    const cls = (on) =>
      `focus-gold inline-flex items-center justify-center gap-2 border px-4 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-150 ${
        on ? "border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-[#E5C158]" : "cursor-not-allowed border-[#27272A] text-[#52525B]"
      }`;
    return (
      <div className="grid gap-3 grid-cols-2">
        <button type="button" disabled={!gated} data-testid="confirm-passenger-sms" onClick={() => confirmAndOpen("sms")} className={cls(gated)}>
          <Send className="h-4 w-4" aria-hidden="true" /> Confirm by SMS
        </button>
        <button type="button" disabled={!gated} data-testid="confirm-passenger-email" onClick={() => confirmAndOpen("email")} className={cls(gated)}>
          <Mail className="h-4 w-4" aria-hidden="true" /> Confirm by Email
        </button>
        <button
          type="button"
          disabled={!enabled}
          data-testid="copy-confirmation-button"
          onClick={() => copyText(passengerConfirm, "Passenger confirmation copied.")}
          className={`focus-gold col-span-2 inline-flex items-center justify-center gap-2 border px-4 py-3 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
            enabled ? "border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]" : "cursor-not-allowed border-[#27272A] text-[#52525B]"
          }`}
        >
          <ClipboardCopy className="h-4 w-4" aria-hidden="true" /> Copy confirmation message
        </button>
      </div>
    );
  };

  const privateDetailsEditor = (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor="pax-name" className={labelCls}>Passenger name</label>
        <input id="pax-name" data-testid="private-pax-name" autoComplete="off" className={inputCls} value={priv.paxName} onChange={setPrivField("paxName")} />
      </div>
      <div>
        <label htmlFor="pax-phone" className={labelCls}>Passenger mobile</label>
        <input id="pax-phone" data-testid="private-pax-phone" type="tel" inputMode="tel" autoComplete="off" className={inputCls} value={priv.paxPhone} onChange={setPrivField("paxPhone")} />
      </div>
      <div>
        <label htmlFor="pax-email" className={labelCls}>Passenger email</label>
        <input id="pax-email" data-testid="private-pax-email" type="email" inputMode="email" autoComplete="off" className={inputCls} value={priv.paxEmail} onChange={setPrivField("paxEmail")} />
      </div>
      <div>
        <label htmlFor="full-pickup" className={labelCls}>Full pickup address</label>
        <input id="full-pickup" data-testid="private-pickup" autoComplete="off" className={inputCls} value={priv.pickup} onChange={setPrivField("pickup")} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="full-dest" className={labelCls}>Full destination</label>
        <input id="full-dest" data-testid="private-destination" autoComplete="off" className={inputCls} value={priv.destination} onChange={setPrivField("destination")} />
      </div>
    </div>
  );

  const sheetCheck = (
    <label className="flex cursor-pointer items-start gap-3 border border-[#27272A] bg-[#111111] p-4 text-sm leading-relaxed text-[#A1A1AA]">
      <input
        type="checkbox"
        data-testid="alloc-check-checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
        checked={allocChecked}
        onChange={(e) => setAllocChecked(e.target.checked)}
      />
      <span>
        <AlertTriangle className="mr-1 inline h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
        I have checked my booking spreadsheet and this job has <strong className="text-white">not</strong> already
        been allocated or confirmed.
      </span>
    </label>
  );

  return (
    <main data-testid="job-share-page" className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <Seo title="Job Manager (Owner)" description="Owner utility for allocating jobs and sharing anonymised summaries with drivers." noindex />

      {/* Compact booking summary + live status */}
      <div className="border border-[rgba(212,175,55,0.4)] bg-[#111111] p-5" data-testid="booking-summary-card">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">Booking {ref}</p>
          {bookingStatus !== "manual" && (
            <p className="border border-[#27272A] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#A1A1AA]" data-testid="booking-status-badge">
              {bookingStatus}
              {assignedDriver ? ` — ${assignedDriver}` : ""}
            </p>
          )}
        </div>
        <p className="mt-2 font-serif text-xl font-semibold text-white">{route}</p>
        <p className="mt-1 text-sm text-[#A1A1AA]">
          {fields.date || "—"} · {fields.time || "—"} · {fields.passengers || "—"} passenger{String(fields.passengers) === "1" ? "" : "s"}
          {fields.luggage ? ` · ${fields.luggage}` : ""} · <span className="text-[#D4AF37]">£100 fixed</span>
        </p>
      </div>

      {/* Owner PIN gate for secure prefill */}
      {bookingKey && pinNeeded && (
        <div className="mt-4 border border-[rgba(212,175,55,0.4)] bg-[#111111] p-5" data-testid="pin-gate">
          <div className="flex items-start gap-3">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
            <div className="w-full">
              <h2 className="text-base font-semibold text-white">Owner access</h2>
              <p className="mt-1 text-sm text-[#A1A1AA]">
                Enter your 6-digit owner PIN to load the passenger's full details securely. The
                link alone is never enough.
              </p>
              <div className="mt-3 flex gap-3">
                <input
                  data-testid="pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={6}
                  placeholder="6-digit PIN"
                  className={inputCls}
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ""))}
                />
                <button
                  type="button"
                  data-testid="pin-unlock-button"
                  onClick={submitPin}
                  className="focus-gold shrink-0 border border-[#D4AF37] bg-[#D4AF37] px-5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-[#E5C158]"
                >
                  Unlock
                </button>
              </div>
              {pinError && <p role="alert" data-testid="pin-error" className="mt-2 text-xs text-[#A1A1AA]">{pinError}</p>}
            </div>
          </div>
        </div>
      )}
      {bookingKey && !pinNeeded && (
        <p className="mt-3 text-xs text-[#71717A]" data-testid="pin-loaded-note">
          Details loaded securely.{" "}
          <button type="button" data-testid="pin-clear" onClick={clearPin} className="text-[#D4AF37] underline-offset-2 hover:underline">
            Forget PIN on this device
          </button>
        </p>
      )}

      {statusError && (
        <p role="alert" data-testid="status-error" className="mt-4 border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">
          {statusError}
        </p>
      )}

      <Section title="Edit journey details" testId="edit-details">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="js-ref" className={labelCls}>Job reference</label>
            <input id="js-ref" data-testid="job-ref-input" autoComplete="off" className={inputCls} placeholder="e.g. M11-AJU" value={fields.reference} onChange={set("reference")} />
          </div>
          <div>
            <label htmlFor="js-passengers" className={labelCls}>Passengers</label>
            <input id="js-passengers" data-testid="job-passengers-input" inputMode="numeric" autoComplete="off" className={inputCls} placeholder="e.g. 2" value={fields.passengers} onChange={set("passengers")} />
          </div>
          <div>
            <label htmlFor="js-from" className={labelCls}>From (broad area only)</label>
            <input id="js-from" data-testid="job-from-input" autoComplete="off" className={inputCls} placeholder="e.g. Great Dunmow area" value={fields.from} onChange={set("from")} />
          </div>
          <div>
            <label htmlFor="js-to" className={labelCls}>To (broad area only)</label>
            <input id="js-to" data-testid="job-to-input" autoComplete="off" className={inputCls} placeholder="e.g. Central London area" value={fields.to} onChange={set("to")} />
          </div>
          <div>
            <label htmlFor="js-date" className={labelCls}>Date</label>
            <input id="js-date" data-testid="job-date-input" autoComplete="off" className={inputCls} placeholder="e.g. Tuesday 21 October" value={fields.date} onChange={set("date")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="js-time" className={labelCls}>Pickup time</label>
              <input id="js-time" data-testid="job-time-input" autoComplete="off" className={inputCls} placeholder="10:30" value={fields.time} onChange={set("time")} />
            </div>
            <div>
              <label htmlFor="js-luggage" className={labelCls}>Luggage</label>
              <input id="js-luggage" data-testid="job-luggage-input" autoComplete="off" className={inputCls} placeholder="Hand luggage" value={fields.luggage} onChange={set("luggage")} />
            </div>
          </div>
        </div>
      </Section>

      {/* Decision — two large buttons */}
      <div className="mt-5 grid grid-cols-2 gap-3" data-testid="decision-buttons">
        <button
          type="button"
          data-testid="ill-drive-button"
          aria-pressed={mode === "self"}
          onClick={() => setMode("self")}
          className={`focus-gold flex min-h-[72px] items-center justify-center gap-2 border p-4 text-base font-semibold uppercase tracking-wider transition-colors duration-150 ${
            mode === "self" ? "border-[#D4AF37] bg-[rgba(212,175,55,0.15)] text-[#D4AF37]" : "border-[#27272A] text-white hover:border-[#3f3f46]"
          }`}
        >
          <Car className="h-5 w-5" aria-hidden="true" /> I'll drive
        </button>
        <button
          type="button"
          data-testid="offer-drivers-button"
          aria-pressed={mode === "drivers"}
          onClick={() => setMode("drivers")}
          className={`focus-gold flex min-h-[72px] items-center justify-center gap-2 border p-4 text-base font-semibold uppercase tracking-wider transition-colors duration-150 ${
            mode === "drivers" ? "border-[#D4AF37] bg-[rgba(212,175,55,0.15)] text-[#D4AF37]" : "border-[#27272A] text-white hover:border-[#3f3f46]"
          }`}
        >
          <Users className="h-5 w-5" aria-hidden="true" /> Find a driver
        </button>
      </div>

      {/* Post-confirmation status actions */}
      {bookingStatus === "confirmed" && (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="post-confirm-actions">
          <button
            type="button"
            data-testid="mark-completed-button"
            onClick={() => transition("completed")}
            className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Mark completed
          </button>
          <button
            type="button"
            data-testid="cancel-booking-button"
            onClick={() => transition("cancelled")}
            className="focus-gold inline-flex items-center justify-center gap-2 border border-[#27272A] px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#A1A1AA] hover:border-red-800 hover:text-red-300"
          >
            <XCircle className="h-4 w-4" aria-hidden="true" /> Cancel booking
          </button>
        </div>
      )}

      {/* I'LL DRIVE flow */}
      {mode === "self" && (
        <div className="mt-5 space-y-4" data-testid="self-flow">
          {bookingStatus === "manual" && (
            <Section title="Passenger contact (stays on this device)" testId="self-private" defaultOpen={!priv.paxPhone && !priv.paxEmail}>
              {privateDetailsEditor}
            </Section>
          )}
          <div className="border border-[#27272A] bg-[#0B0B0B] p-5">
            <h2 className="text-base font-semibold text-white">Confirm the journey</h2>
            <p className="mt-1 text-sm text-[#A1A1AA]">
              You keep the full £100 — no referral fee applies when you drive. The passenger pays
              you in the cab, cash or card, at the end of the journey.
            </p>
            <div className="mt-4">{sheetCheck}</div>
            <div className="mt-4"><ConfirmButtons enabled={canConfirmSelf} /></div>
            <p className="mt-4 text-xs leading-relaxed text-[#71717A]" data-testid="tracker-reminder">
              Then record it: booking spreadsheet → status "Confirmed", completed by owner. After
              the journey: "Completed".
            </p>
          </div>
        </div>
      )}

      {/* FIND A DRIVER flow */}
      {mode === "drivers" && (
        <div className="mt-5 space-y-4" data-testid="drivers-flow">
          <div className="border border-[#27272A] bg-[#0B0B0B] p-5">
            <h2 className="text-base font-semibold text-white">Share with your driver group</h2>
            {!ownerNumber.trim() && (
              <div className="mt-3">
                <label htmlFor="owner-wa" className={labelCls}>Your WhatsApp number (one-tap driver replies)</label>
                <input
                  id="owner-wa"
                  data-testid="owner-number-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  className={inputCls}
                  placeholder="e.g. 07700 900000"
                  value={ownerNumber}
                  onChange={(e) => saveOwnerNumber(e.target.value)}
                />
              </div>
            )}
            {ownerNumber.trim() && (
              <p className="mt-3 text-xs text-[#71717A]">
                Reply link uses {ownerNumber} (this device only).{" "}
                <button type="button" data-testid="owner-number-clear" onClick={() => saveOwnerNumber("")} className="text-[#D4AF37] underline-offset-2 hover:underline">
                  Clear
                </button>
              </p>
            )}
            <div role="group" aria-label="Message format" className="mt-4 grid grid-cols-2 gap-2">
              {[{ value: "short", label: "Short" }, { value: "full", label: "Full" }].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`message-format-${opt.value}`}
                  aria-pressed={format === opt.value}
                  onClick={() => setFormat(opt.value)}
                  className={`focus-gold border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
                    format === opt.value ? "border-[#D4AF37] bg-[rgba(212,175,55,0.15)] text-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <pre data-testid="job-message-preview" className="mt-3 whitespace-pre-wrap border border-[#27272A] bg-[#111111] p-4 font-sans text-sm leading-relaxed text-[#D4D4D8]">
              {message}
            </pre>
            <div className="mt-4 grid gap-3 grid-cols-2">
              <button
                type="button"
                onClick={shareWhatsApp}
                data-testid="share-whatsapp-button"
                className="focus-gold inline-flex items-center justify-center gap-2 border border-[#25D366] bg-[#25D366] px-4 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#3be07a]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
              </button>
              <button
                type="button"
                onClick={() => copyText(message, "Job summary copied — paste it into your driver chat.")}
                data-testid="copy-job-details-button"
                className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] bg-transparent px-4 py-4 text-sm font-semibold uppercase tracking-wider text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37] hover:text-black"
              >
                <ClipboardCopy className="h-4 w-4" aria-hidden="true" /> Copy
              </button>
            </div>
            <p className="mt-3 text-xs text-[#71717A]">
              Driver replies arrive as private WhatsApp messages with their £20 fee acceptance.
              Interest is not a reservation — allocation is manual.
            </p>
          </div>

          <div className="border border-[#27272A] bg-[#0B0B0B] p-5" data-testid="allocation-section">
            <h2 className="text-base font-semibold text-white">Allocate to a driver</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="drv-name" className={labelCls}>Driver name</label>
                <input id="drv-name" data-testid="driver-name-input" autoComplete="off" className={inputCls} value={driver.name} onChange={setDrvField("name")} />
              </div>
              <div>
                <label htmlFor="drv-phone" className={labelCls}>Driver mobile</label>
                <input id="drv-phone" data-testid="driver-phone-input" type="tel" inputMode="tel" autoComplete="off" className={inputCls} value={driver.phone} onChange={setDrvField("phone")} />
              </div>
            </div>
            <label className="mt-4 flex cursor-pointer items-start gap-3 border border-[#27272A] bg-[#111111] p-4 text-sm leading-relaxed text-[#A1A1AA]">
              <input
                type="checkbox"
                data-testid="driver-verified-checkbox"
                className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
                checked={driverVerified}
                onChange={(e) => setDriverVerified(e.target.checked)}
              />
              <span>
                I have checked this driver is <strong className="text-white">Approved</strong> in my
                register, with a valid TfL Green Badge licence in date.
              </span>
            </label>
            <div className="mt-3">{sheetCheck}</div>

            {bookingStatus === "manual" && (
              <div className="mt-4">
                <Section title="Private booking details (entered once, stays on this device)" testId="driver-private">
                  {privateDetailsEditor}
                </Section>
              </div>
            )}

            <div className="mt-4 space-y-3">
              <ConfirmButtons enabled={canConfirmDriver} />
              <button
                type="button"
                disabled={!canConfirmDriver}
                data-testid="send-driver-details"
                onClick={() =>
                  window.open(
                    `https://wa.me/${normalisePhone(driver.phone)}?text=${encodeURIComponent(driverDetails)}`,
                    "_blank",
                    "noopener"
                  )
                }
                className={`focus-gold inline-flex w-full items-center justify-center gap-2 border px-4 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-150 ${
                  canConfirmDriver ? "border-[#25D366] bg-[#25D366] text-black hover:bg-[#3be07a]" : "cursor-not-allowed border-[#27272A] text-[#52525B]"
                }`}
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" /> Send details to driver
              </button>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-[#71717A]" data-testid="tracker-reminder">
              Then record it: booking spreadsheet → status "Confirmed", allocated driver name;
              after the journey → "Completed" and whether the £20 referral fee arrived (bank
              transfer, within 24 hours of completion). The sheet remains the full record.
            </p>
          </div>

          <div
            data-testid="job-share-privacy-note"
            className="flex items-start gap-3 border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] p-4"
          >
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-[#A1A1AA]">
              Group messages contain area names only — never passenger names, phone numbers,
              email addresses, house numbers, exact addresses or full postcodes. Full details go
              only to the selected driver, privately.
            </p>
          </div>
        </div>
      )}
    </main>
  );
}
